import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, SchoolLevel } from '@jeraisonne/db';
import type { AppStudentObjective } from './objective-types';

/**
 * Returns the list of exercise IDs that match an objective's filters.
 * Filters applied (in priority order):
 *   1. chapters.level — filtré sur le niveau de l'élève dès qu'il est connu (absolu)
 *   2. selected_chapter_id (explicit chapter selection)
 *   3. selected_subject (matière choisie par l'élève)
 *   4. template.subject (matière fixée par le template)
 *
 * Note: exam_type in template.filters is informational in V1 (no column on exercises yet).
 */
export async function getExerciseIdsForObjective(
  supabase: SupabaseClient<Database>,
  objective: AppStudentObjective,
  studentLevel?: SchoolLevel | null,
): Promise<string[]> {
  const { template, selectedSubject, selectedChapterId } = objective;

  let query = supabase
    .from('exercises')
    .select('id, chapter_id, chapters!inner ( level, subject )')
    .eq('status', 'published');

  if (template.type === 'warmup') {
    query = query.eq('is_fundamental', true);
    if (!selectedChapterId) return [];
  }

  // Objectif Diagnostic : tous les exercices is_diagnostic du niveau courant
  // (≈ 1 par chapitre). Le niveau est indispensable — sinon on ne liste rien.
  // `is_diagnostic` est postérieur aux types stale → cast de la colonne.
  if (template.type === 'diagnostic') {
    if (!studentLevel) return [];
    query = query.eq('is_diagnostic' as 'is_fundamental', true);
  }

  if (studentLevel) {
    query = query.eq('chapters.level', studentLevel);
  }

  if (selectedChapterId) {
    query = query.eq('chapter_id', selectedChapterId);
  }

  const targetSubject = selectedSubject ?? template.subject ?? null;
  if (targetSubject) {
    query = query.eq('chapters.subject', targetSubject);
  }

  const { data } = await query;
  return (data ?? []).map((e) => e.id);
}
